/* eslint-disable ngrx/avoid-dispatching-multiple-actions-sequentially */
/* eslint-disable ngrx/avoid-mapping-selectors */
/* eslint-disable ngrx/prefer-action-creator-in-dispatch */
import { Injectable } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { format, isSameDay } from 'date-fns';
import { flatMap as _flatmap } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map, mergeMap, take, toArray } from 'rxjs/operators';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { AutoPayUtilService } from '@amfam/billing/auto-pay/feature';
import { AutoPayUtilSelectors } from '@amfam/billing/auto-pay/util';
import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import {
  PaymentMethodActions,
  PaymentMethodSelectors
} from '@amfam/billing/payment-method/data-access';
import {
  PaymentMethod,
  PaymentMethodAccountType,
  PaymentMethodUtilService
} from '@amfam/billing/payment-method/util';
import { PaymentAccountSelectors } from '@amfam/billing/paymentaccount/data-access';
import { PaymentConfirmationSelectors } from '@amfam/billing/paymentconfirmation/data-access';
import {
  pendingPaymentActions,
  pendingPaymentQuery
} from '@amfam/billing/pending-payment/data-access';
import {
  ScheduledPaymentActions,
  ScheduledPaymentSelectors
} from '@amfam/billing/schedulepayment/data-access';
import { PaymentTermsAndConditionsType } from '@amfam/billing/shared/ui';
import { BillAccountTransmuteService } from '@amfam/billing/shared/util';
import { env } from '@amfam/environment';
import { PolicySelectors } from '@amfam/policy/data-access';
import { PolicyTypeIconConstants } from '@amfam/policy/models';
import {
  AutomaticPayment,
  BillAccount,
  BillingPaymentPaths,
  ONLINE_BILLING,
  PaymentAccount,
  PaymentMethodRadioOption,
  ScheduledPayment,
  SchedulePaymentPayloadModel
} from '@amfam/shared/models';
import { ArrayToList, CurrencyUSDPipe } from '@amfam/shared/ui/pipes';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  AlertModalConfig,
  DsModalService,
  LoadingSpinnerService,
  Option,
  ValidationService
} from '@amfam/ui-kit';

import { CommonFeatureFacade } from '../../../../../shared/feature/src/lib/+state/common.facade'; // TODO temp fix for circular dependency
import { OneTimePayUtilService } from '../util/one-time-pay-util.service';
import * as OneTimePayActions from './one-time-pay-feature.actions';
import {
  AccountForSimplifiedPayment,
  AlertMessage,
  OneTimePaySetup,
  OTPAnalyticsModel,
  OTPReviewData,
  PaymentOption,
  simplifiedPaymentReviewAccount,
  simplifiedPaymentReviewData
} from './one-time-pay-feature.models';
import * as OneTimePaySelectors from './one-time-pay-feature.selectors';

@Injectable({ providedIn: 'root' })
export class OneTimePayFeatureFacade {
  constructor(
    private store: Store,
    private oneTimePayUtil: OneTimePayUtilService,
    private paymentMethodUtil: PaymentMethodUtilService,
    private spinner: LoadingSpinnerService,
    private billAccountTransmuteService: BillAccountTransmuteService,
    private autoPayUtil: AutoPayUtilService,
    private modalService: DsModalService,
    private commonFacade: CommonFeatureFacade,
    private copyService: CopyService,
    private featureFlagService: FeatureFlagService
  ) {}

  getScheduledPayments() {
    return this.store.select(ScheduledPaymentSelectors.selectScheduledPayments);
  }

  getPaymentMethodOptions(): Observable<Option[]> {
    return this.store.select(PaymentMethodSelectors.selectPaymentMethods).pipe(
      map(paymentMethods => {
        const filteredPaymentMethods = paymentMethods.filter(function (x) {
          return x !== undefined;
        });
        return this.oneTimePayUtil.getPaymentMethodOptions(filteredPaymentMethods);
      })
    );
  }

  buildPaymentMethodOptions(): Observable<PaymentMethodRadioOption[]> {
    return this.store.select(PaymentMethodSelectors.selectPaymentMethods).pipe(
      map(paymentMethods => {
        const filteredPaymentMethods = paymentMethods.filter(function (x) {
          return x !== undefined;
        });
        return this.oneTimePayUtil.buildPaymentMethodOptions(filteredPaymentMethods);
      })
    );
  }

  getBillAccountsList(): Observable<OneTimePaySetup[]> {
    return combineLatest([
      this.store.select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay),
      this.store.select(OneTimePaySelectors.selectOneTimePayEntities)
    ]).pipe(
      map(([eligibleAccounts, oneTimePayEntities]) =>
        eligibleAccounts
          .filter(eligibleAccount => oneTimePayEntities[eligibleAccount.billAccountNumber])
          .map(acc => {
            const option: OneTimePaySetup = {
              policyTypeIcon: acc.icon,
              riskDescription: new ArrayToList().transform(
                _flatmap(acc.policyList, policy => policy.riskDescriptionList)
              ),
              selectedPaymentAmount:
                oneTimePayEntities[acc.billAccountNumber].selectedPaymentAmount,
              billAccountNumber: acc.billAccountNumber,
              minDue: acc.minimumAmountDue,
              helpText: { text: new CurrencyUSDPipe().transform(acc.minimumAmountDue) },
              paymentAccountId: 'Selected Payment Account Id',
              dueDate: acc.paymentDueDate,
              pastDue: acc.pastDue,
              accountBalance: acc.accountBalance
            };
            return option;
          })
      )
    );
  }

  getBillAccountsWithAlertMessageList(): Observable<OneTimePaySetup[]> {
    return this.getBillAccountsList().pipe(
      take(1),
      mergeMap(entites => entites),
      mergeMap(entity =>
        this.getAlertMessage(entity.billAccountNumber, entity.selectedPaymentAmount, entity)
      ),
      map(alertObj => {
        const otpEntity = alertObj.entity;
        otpEntity.text = alertObj.alertMessage?.warningText;
        otpEntity.alertMessage = alertObj.alertMessage;
        otpEntity.helpText = {
          text: new CurrencyUSDPipe().transform(otpEntity.minDue),
          warning:
            alertObj.alertMessage?.warningText === 'This account is past due'
              ? true
              : otpEntity.selectedPaymentAmount === 0
                ? true
                : false,
          errorText: otpEntity.selectedPaymentAmount === 0 ? 'Please enter a payment amount' : null
        };
        return otpEntity;
      }),
      toArray()
    );
  }

  getAlertMessage(
    billAccountNumber: string,
    amount: number,
    entity?: OneTimePaySetup,
    simplifiedPaymentEntity?: AccountForSimplifiedPayment
  ): Observable<{
    alertMessage: AlertMessage;
    entity: OneTimePaySetup;
    simplifiedPaymentEntity: AccountForSimplifiedPayment;
  }> {
    return combineLatest([
      this.store.select(ScheduledPaymentSelectors.selectScheduledPayments),
      this.store.select(BillAccountsSelectors.selectAllBillAccounts),
      this.store.select(AutoPaySelectors.getAutomaticPayments)
    ]).pipe(
      take(1),
      map(([Payments, billAccounts, autoPayAccounts]) => ({
        alertMessage: this.oneTimePayUtil.buildAlertMessage(
          Payments,
          billAccounts,
          autoPayAccounts,
          billAccountNumber,
          amount
        ),
        entity: entity,
        simplifiedPaymentEntity: simplifiedPaymentEntity
      }))
    );
  }
  /**
   * @description: funtion to set the one time pay page information in the feature state
   */
  addOTPReviewInfo(OTPPageDataArr: OTPReviewData[]) {
    this.store.dispatch(OneTimePayActions.setOneTimePayReviewInfo({ reviewData: OTPPageDataArr }));
  }

  /**
   * @description: funtion to set the one time pay page information in the feature state
   */
  addOTPReviewPaymentAmtAnalyticsInfo(paymentAmt: string) {
    this.store.dispatch(
      OneTimePayActions.setOneTimePayPaymentAmtAnalyticsInfo({ paymentAmtForAnalytics: paymentAmt })
    );
  }

  /**
   * @description: funtion to set the one time pay page information in the feature state
   */
  addOTPReviewPaymentDateAnalyticsInfo(paymentDate: string) {
    this.store.dispatch(
      OneTimePayActions.setOneTimePayPaymentDateAnalyticsInfo({
        paymentDateForAnalytics: paymentDate
      })
    );
  }

  /**
   * @description: funtion to set the one time pay page information in the feature state
   */
  getOTPReviewPaymentAmtAnalyticsInfo() {
    return this.store.select(OneTimePaySelectors.selectOneTimePayAnalyticsPaymentAmt);
  }

  /**
   * @description: funtion to set the one time pay page information in the feature state
   */
  getOTPReviewPaymentDateAnalyticsInfo() {
    return this.store.select(OneTimePaySelectors.selectOneTimePayAnalyticsPaymentDate);
  }

  /**
   * @description: funtion to dispatch the submission of OTP action
   */
  submitOTP() {
    this.store.dispatch(OneTimePayActions.submitOTP());
  }

  /**
   * @description: funtion to dispatch the submission of OTP action
   */
  editOTP() {
    this.store.dispatch(OneTimePayActions.editOTP());
  }

  initSingleOneTimePay() {
    this.store
      .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
      .pipe(take(1))
      .subscribe(billAccounts => {
        if (billAccounts.length === 1) {
          this.store.dispatch(
            OneTimePayActions.addOneTimePayBillAccount({
              setups: {
                billAccountNumber: billAccounts[0].billAccountNumber,
                selectedPaymentAmount: Number(billAccounts[0].minimumAmountDue),
                dueDate: billAccounts[0].paymentDueDate
              }
            })
          );
        }
      });
  }

  getOTPEntities() {
    return this.store.select(OneTimePaySelectors.selectOneTimePayEntities);
  }

  isEditOTP() {
    return this.store.select(OneTimePaySelectors.selectOneTimePayEntities).pipe(
      map(otpEntities => {
        const key = Object.keys(otpEntities)[0];
        return !!otpEntities[key]?.scheduledPaymentId;
      })
    );
  }

  getBillAccountEntities() {
    return this.store.select(BillAccountsSelectors.selectBillAccountEntities);
  }

  getScheduledPaymentEntities() {
    return this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentEntities);
  }

  getPaymentId() {
    return this.store.select(OneTimePaySelectors.selectPaymentId);
  }

  getBillAccount(billAccountNumber: string) {
    return this.store.select(BillAccountsSelectors.selectBillAccount(billAccountNumber));
  }

  getPaymentAccountEntities() {
    return <Observable<Dictionary<PaymentAccount>>>(
      (this.store.select(PaymentAccountSelectors.selectPaymentAccountEntities) as unknown)
    );
  }

  getCustomerEmailAddress() {
    return this.store.select(userQuery.selectEmailAddress);
  }

  getConfirmationObj() {
    return this.store.select(PaymentConfirmationSelectors.selectPaymentConfirmationDataState);
  }

  getScheduledPaymentsError() {
    return this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentsError);
  }

  getScheduledPaymentOperationAndStatus(): Observable<
    'Success' | 'Error' | 'Delete-Success' | 'Delete-Error'
  > {
    return this.store.select(OneTimePaySelectors.selectOneTimePayOperation);
  }

  getOTPDateType(): Observable<'Future' | 'Immediate'> {
    return this.store.select(OneTimePaySelectors.selectOneTimePayEntities).pipe(
      map(otpEntities => {
        for (const key in otpEntities) {
          if (isSameDay(new Date(), otpEntities[key].selectedPaymentDate)) {
            return 'Immediate';
          } else {
            return 'Future';
          }
        }
        return 'Future';
      })
    );
  }

  showAutoPaySetUpLink(): Observable<boolean> {
    return combineLatest([
      this.store.select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay),
      this.store.select(AutoPaySelectors.getAutomaticPayments),
      this.store.select(routerQuery.getRouterCurrentStateUrl)
    ]).pipe(
      take(1),
      map(([accounts, autoPayAcc, currentStateUrl]) => {
        const doesNotHavePastDue = accounts.filter(acc => !acc.pastDue);
        const hasAutoPay: AutomaticPayment[] = [];
        doesNotHavePastDue.map(noPastDueAcc => {
          autoPayAcc.find(ap => {
            if (ap?.billAccountNumber === noPastDueAcc.billAccountNumber) {
              hasAutoPay.push(ap);
            }
          });
        });
        if (accounts.length === 1) {
          // Show AutopaySetup link only if account is eligible for autopay setup
          if (accounts[0].pastDue || hasAutoPay.length > 0) {
            return false;
          } else {
            return true;
          }
        } else {
          // Show AutopaySetup link for multiple accounts only on choose accounts screen and not on review payment screen and if any account is eligible for autopay setup
          if (
            currentStateUrl.includes('one-time-pay/choose-accounts') &&
            doesNotHavePastDue.length > 0 &&
            doesNotHavePastDue.length !== hasAutoPay.length
          ) {
            return true;
          } else {
            return false;
          }
        }
      })
    );
  }

  deletePayment(paymentInfoObj: SchedulePaymentPayloadModel) {
    this.store.dispatch(ScheduledPaymentActions.scheduledPaymentsDelete(paymentInfoObj));
  }

  getDefaultPayment(billAccountNumber: string) {
    return this.store
      .select(OneTimePaySelectors.selectOneTimePayEntities)
      .pipe(map(otpEntities => otpEntities[billAccountNumber].selectedPaymentAmount));
  }

  setupEditOTP(paymentId: string, scheduledPaymentEntities: Dictionary<ScheduledPayment>) {
    this.store.dispatch(
      OneTimePayActions.setOneTimePayReviewInfoForEdit({
        reviewData: this.oneTimePayUtil.buildEditOTPReviewData(paymentId, scheduledPaymentEntities)
      })
    );
  }

  sendAnalytics(analyticsData: OTPAnalyticsModel) {
    this.store.dispatch(OneTimePayActions.sendOtpAnalytics({ payload: analyticsData }));
  }

  showSchedulePaymentButton(): Observable<boolean> {
    return combineLatest([
      this.store.select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay),
      this.store.select(pendingPaymentQuery.selectPendingPayments)
    ]).pipe(
      map(([billAccountsEligibleForOTP, pendingPayments]) =>
        billAccountsEligibleForOTP.some(billAccount =>
          pendingPayments
            .map(pendingPayment => pendingPayment.billAccountNumber)
            .includes(billAccount.billAccountNumber)
        )
      )
    );
  }

  registerBillAccountsBeforeOneTimePay(
    billAccounts: BillAccount[],
    selectedAccounts?: {
      billAccountNumber: string;
      selectedPaymentAmount: string;
      dueDate: string;
      paymentTerm: string;
    }[],
    reviewPaymentInfo?: simplifiedPaymentReviewData
  ): void {
    this.spinner.start({
      blockActions: true
    });

    // get bill accounts that haven't been registered
    const unregisteredBillAccountNumbers: string[] = billAccounts
      .filter(billAccount => billAccount.billingMethod !== ONLINE_BILLING)
      .map(billAccount => billAccount.billAccountNumber);

    // get bill accounts that have already been registered
    const previouslyRegisteredBillAccounts: BillAccount[] = billAccounts.filter(
      billAccount => billAccount.billingMethod === ONLINE_BILLING
    );

    // register unregistered bill accounts
    this.billAccountTransmuteService
      .initiateSilentRegistration(unregisteredBillAccountNumbers)
      .pipe(take(1))
      .subscribe((successfullyRegisteredBillAccounts: BillAccount[]) => {
        const registeredAccounts: BillAccount[] = previouslyRegisteredBillAccounts.concat(
          successfullyRegisteredBillAccounts
        );
        const registrationError: boolean =
          successfullyRegisteredBillAccounts.length < unregisteredBillAccountNumbers.length;

        this.spinner.stop();

        // proceed to one-time-pay setup if we have any accounts to set up
        if (registeredAccounts.length > 0) {
          if (this.featureFlagService.isEnabled('simplified_payment')) {
            const registeredAccountsForOneTimePay: {
              billAccountNumber: string;
              selectedPaymentAmount: string;
              dueDate: string;
              paymentTerm: string;
            }[] = [];
            registeredAccounts.forEach(registeredAccount => {
              selectedAccounts.map(account => {
                if (account.billAccountNumber === registeredAccount.billAccountNumber) {
                  registeredAccountsForOneTimePay.push(account);
                }
              });
            });

            // remove bill accounts selection from state just before adding them as per new selection - this is when user go back n forth b/w pages within the flow
            this.store.dispatch(OneTimePayActions.removeOneTimePayBillAccounts());
            this.store.dispatch(pendingPaymentActions.ClearPendingPaymentsAction());
            registeredAccountsForOneTimePay.forEach(account => {
              this.store.dispatch(
                pendingPaymentActions.AddPendingPaymentAction({
                  billAccountNumber: account.billAccountNumber
                })
              );
              this.store.dispatch(
                OneTimePayActions.addOneTimePayBillAccount({
                  setups: {
                    billAccountNumber: account.billAccountNumber,
                    selectedPaymentAmount: Number(account.selectedPaymentAmount),
                    dueDate: account.dueDate,
                    selectedPaymentAccount: reviewPaymentInfo?.paymentMethodNickName,
                    selectedPaymentDate: reviewPaymentInfo?.paymentDate,
                    paymentTerm: account.paymentTerm
                  }
                })
              );
            });
          } else {
            registeredAccounts.forEach(registeredAccount => {
              this.store.dispatch(
                OneTimePayActions.addOneTimePayBillAccount({
                  setups: {
                    billAccountNumber: registeredAccount.billAccountNumber,
                    selectedPaymentAmount: Number(registeredAccount.minimumAmountDue),
                    dueDate: registeredAccount.paymentDueDate
                  }
                })
              );
            });
          }
          if (registrationError) {
            this.modalService.open('silentRegistrationErrorModalSomeFailed');
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [
                  this.featureFlagService.isEnabled('simplified_payment_p2')
                    ? BillingPaymentPaths.SIMPLIFIED_PAYMENT_REVIEW
                    : BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT
                ]
              })
            );
          }
        } else {
          this.modalService.open('silentRegistrationErrorModalAllFailed');
        }
      });
  }

  registerBillAccountsBeforeOneTimePayPhoenix(
    reviewPaymentInfo?: simplifiedPaymentReviewData
  ): void {
    this.spinner.start({
      blockActions: true
    });

    this.store.dispatch(
      OneTimePayActions.addOneTimePayBillAccount({
        setups: {
          selectedPaymentAmount: Number(reviewPaymentInfo.totalAmount),
          dueDate: reviewPaymentInfo.paymentDate,
          iframeToken: reviewPaymentInfo.iframeToken,
          billAccountNumber: reviewPaymentInfo.policyNumber,
          cardDetails: reviewPaymentInfo.cardDetails
        }
      })
    );

    this.spinner.stop();

    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.SIMPLIFIED_PAYMENT_REVIEW]
      })
    );
  }

  getPaymentErrorCode(): Observable<string> {
    return this.store.select(OneTimePaySelectors.selectPaymentErrorCode);
  }

  getSimplifiedPaymentBillAccounts(): Observable<AccountForSimplifiedPayment[]> {
    return combineLatest([
      this.store.select(pendingPaymentQuery.selectPendingPaymentIds),
      this.store.select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay),
      this.store.select(AutoPaySelectors.getAutomaticPayments),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentsWithMostPaymentStatus),
      this.store.select(PolicySelectors.selectPropertyPoliciesWithTPI),
      this.store.select(OneTimePaySelectors.selectOneTimePayEntities),
      this.store.select(BillAccountsSelectors.selectBillAccounts)
    ]).pipe(
      take(1),
      map(
        ([
          preselectedAccounts,
          eligibleBillAccounts,
          automaticPayments,
          scheduledPayments,
          policiesWithTPI,
          oneTimePayEntities,
          billAccounts
        ]) =>
          eligibleBillAccounts.map(billAccount => {
            const isTPI = policiesWithTPI.find(
              policy => policy.billingAccountNumber === billAccount.billAccountNumber
            );
            const option: AccountForSimplifiedPayment = {
              nickName: billAccount?.billingPreferences?.accountNickName
                ? billAccount?.billingPreferences?.accountNickName
                : `Account ending in ${billAccount.billAccountNumber.substring(
                    billAccount.billAccountNumber.length - 4
                  )}`,
              isSelected:
                preselectedAccounts.includes(billAccount.billAccountNumber) ||
                eligibleBillAccounts.length === 1,
              id: billAccount.billAccountNumber,
              pillStatus: this.commonFacade.getAccountStatus(
                billAccount,
                scheduledPayments,
                automaticPayments,
                isTPI
              ),
              policyRiskIconList: _flatmap(
                billAccount.policyList,
                policy => policy.riskIconList
              ).filter(list => list !== undefined),
              riskDescription: new ArrayToList().transform(
                _flatmap(billAccount.policyList, list => list.riskDescriptionList),
                '; '
              ),
              paymentOptions: [
                {
                  amount: new CurrencyUSDPipe().transform(billAccount.minimumAmountDue),
                  id: 'minDue',
                  text: this.getMinimumDueText(billAccount)
                },
                {
                  amount: new CurrencyUSDPipe().transform(this.getFullPayValue(billAccount)),
                  id: 'accountBalance',
                  text: this.getFullPayText(billAccount)
                },
                {
                  amount: new CurrencyUSDPipe().transform(
                    this.getOtherAmountValue(billAccount, oneTimePayEntities)
                  ),
                  id: 'otherAmount',
                  text: 'Other amount'
                }
              ],
              selectedPaymentAmount:
                oneTimePayEntities[billAccount.billAccountNumber]?.selectedPaymentAmount.toString(),
              dueDate: billAccount.paymentDueDate,
              minDue: billAccount.minimumAmountDue,
              accountType: billAccount.accountType,
              isCancellationLetterSent: billAccount.cancelLetterSent,
              pastDue: billAccount.pastDue,
              linkedAccountIcon: billAccount.associated
                ? null
                : PolicyTypeIconConstants.UNASSOCIATED,
              isRomeNew: false,
              isRomeOld: false,
              icon: billAccount.icon
            };
            if (Number(billAccount.minimumAmountDue) === 0) {
              option.paymentOptions.splice(0, 1);
            }
            if (option.riskDescription === '' && !billAccount.associated) {
              option.riskDescription = this.copyService.getCopy(
                'billing.simplifiedPayment.linkedAccountRiskDiscription'
              );
            }

            let pairFound = false;

            // Finding a bill account pair based on if the policy number is repeated in any other bill acc
            if (billAccounts.every(billAc => billAc.policyList)) {
              for (const policy of billAccount.policyList) {
                if (pairFound) {
                  break; // No need to look for other policies once a pair is found in previous iteration
                }

                for (const billAcc of billAccounts) {
                  // Preventing self match
                  if (billAcc.billAccountNumber !== billAccount.billAccountNumber) {
                    // if policy.policyNumber is present in the policyList of billAcc then pairFound is true
                    if (billAcc.policyList.some(p => p.policyNumber === policy.policyNumber)) {
                      pairFound = true;
                      break; // No need to look for other bill accounts once a pair bill account is found
                    }
                  }
                }
              }
            }

            if (pairFound) {
              option.isRomeNew = billAccount.isAdvance;
              option.isRomeOld = !billAccount.isAdvance;
            }

            return option;
          })
      )
    );
  }

  getSimplifiedPaymentBillAccountsWithAlertMessageList(): Observable<
    AccountForSimplifiedPayment[]
  > {
    return this.getSimplifiedPaymentBillAccounts().pipe(
      take(1),
      mergeMap(entites => entites),
      mergeMap(entity =>
        this.getAlertMessage(
          entity.id,
          Number(entity.selectedPaymentAmount?.replace(/\$|,/g, '')),
          null,
          entity
        )
      ),
      map(alertObj => {
        const simplifiedPaymentEntity = alertObj.simplifiedPaymentEntity;
        simplifiedPaymentEntity.alertMessage = alertObj.alertMessage;
        return simplifiedPaymentEntity;
      }),
      toArray()
    );
  }

  getUpdatedSimplifiedPaymentBillAccounts(
    billAccountOptions: Observable<AccountForSimplifiedPayment[]>,
    billAccountOption: AccountForSimplifiedPayment
  ): Observable<AccountForSimplifiedPayment[]> {
    return billAccountOptions.pipe(
      take(1),
      mergeMap(entites => entites),
      mergeMap(entity =>
        this.getAlertMessage(
          entity.id,
          entity.id === billAccountOption.id
            ? Number(billAccountOption.selectedPaymentAmount?.replace(/\$|,/g, ''))
            : Number(entity.selectedPaymentAmount?.replace(/\$|,/g, '')),
          null,
          entity
        )
      ),
      map(alertObj => {
        const simplifiedPaymentEntity = alertObj.simplifiedPaymentEntity;
        simplifiedPaymentEntity.alertMessage = alertObj.alertMessage;
        if (simplifiedPaymentEntity.id === billAccountOption.id) {
          simplifiedPaymentEntity.selectedPaymentAmount = billAccountOption.selectedPaymentAmount;
          simplifiedPaymentEntity.radioFieldErrorMessage = billAccountOption.radioFieldErrorMessage;
          simplifiedPaymentEntity.paymentOptions.map(paymentOption => {
            if (paymentOption.id === 'otherAmount') {
              paymentOption.amount = billAccountOption.selectedPaymentAmount;
            }
          });
        }
        return simplifiedPaymentEntity;
      }),
      toArray()
    );
  }

  getMinimumDueText(billaccount: BillAccount) {
    if (!billaccount.isAdvance && billaccount.cancelLetterSent) {
      return 'Past due + Current';
    }
    if (billaccount.pastDue) {
      return 'Past due';
    }
    return 'Minimum due';
  }

  getFullPayValue(billaccount: BillAccount) {
    if (billaccount.isAdvance && Number(billaccount?.fullPayBalance) > 0) {
      return billaccount?.fullPayBalance;
    }
    return billaccount?.accountBalance;
  }

  getFullPayText(billaccount: BillAccount) {
    if (billaccount.isAdvance && Number(billaccount?.fullPayBalance) > 0) {
      return 'Full pay amount';
    }
    return 'Account balance';
  }

  getOtherAmountValue(billAccount: BillAccount, oneTimePayEntities: Dictionary<OneTimePaySetup>) {
    if (
      oneTimePayEntities[billAccount.billAccountNumber]?.selectedPaymentAmount ===
        Number(billAccount.minimumAmountDue) ||
      oneTimePayEntities[billAccount.billAccountNumber]?.selectedPaymentAmount ===
        Number(billAccount.accountBalance) ||
      oneTimePayEntities[billAccount.billAccountNumber]?.selectedPaymentAmount ===
        Number(billAccount.fullPayBalance)
    ) {
      return '0.00';
    } else {
      return oneTimePayEntities[billAccount.billAccountNumber]?.selectedPaymentAmount.toString();
    }
  }

  setOtherAmountValidator(option: AccountForSimplifiedPayment, formGroup: FormGroup) {
    const validatorsList = [
      ValidationService.paymentAmountValidation(true),
      ValidationService.paymentAmountMaxValidation({
        balance: this.getAccountBalance(option.paymentOptions),
        accountType: option.accountType
      })
    ];
    if (option.pastDue) {
      validatorsList.push(
        ValidationService.minimumDuePaymentPastDueValidator(
          'otherAmount',
          option.paymentOptions[0].amount,
          option.isCancellationLetterSent
        )
      );
    }
    formGroup.controls[option.id]
      .get('otherAmount')
      .setValidators(Validators.compose([...validatorsList]));
  }

  getAccountBalance(paymentOptions: PaymentOption[]): number {
    const accountBalanceOption = paymentOptions.find(option => option.id === 'accountBalance');
    return parseFloat(accountBalanceOption?.amount?.replace(/\$|,/g, ''));
  }

  cancelModalConfig(): AlertModalConfig {
    return {
      headerText: this.copyService.getCopy('billing.autoPayRefactor.cancelModalHeading'),
      bodyText: this.copyService.getCopy('billing.oneTimePayRefactor.cancelModalBody'),
      modalId: 'oneTimePayCancelModal',
      ctaConfig: {
        primaryButtonName: "I'm sure",
        primaryButtonDestructive: true,
        tertiaryButtonName: 'Cancel',
        fullWidthButtons: true
      }
    };
  }

  silentRegistrationAllFailedError(): AlertModalConfig {
    return {
      headerText: this.copyService.getCopy(
        'billing.oneTimePayRefactor.silentRegistrationErrorHeaderText'
      ),
      bodyText: this.copyService.getCopy(
        'billing.oneTimePayRefactor.silentRegistrationErrorBodyTextAllFailed'
      ),
      modalId: 'silentRegistrationErrorModalAllFailed',
      ctaConfig: {
        primaryButtonName: 'OK',
        fullWidthButtons: true
      }
    };
  }

  silentRegistrationPartialError(): AlertModalConfig {
    return {
      headerText: this.copyService.getCopy(
        'billing.oneTimePayRefactor.silentRegistrationErrorHeaderText'
      ),
      bodyText: this.copyService.getCopy(
        'billing.oneTimePayRefactor.silentRegistrationErrorBodyTextSomeFailed'
      ),
      modalId: 'silentRegistrationErrorModalSomeFailed',
      ctaConfig: {
        primaryButtonName: 'Continue',
        secondaryButtonName: 'Ok',
        fullWidthButtons: true
      }
    };
  }

  sendSimplifiedPaymentAnalytics(): void {
    combineLatest([this.getBillAccountsList(), this.getBillAccountEntities()])
      .pipe(
        map(([otpEntities, billAccounts]) => ({ otpEntities, billAccounts })),
        take(1)
      )
      .subscribe(({ otpEntities, billAccounts }) => {
        const analyticsAmtText: string[] = [];
        otpEntities.map(entity => {
          const matchingBillAccount = billAccounts[entity.billAccountNumber];
          switch (Number(entity.selectedPaymentAmount)) {
            case Number(matchingBillAccount?.minimumAmountDue):
              analyticsAmtText.push('Minimum Due');
              break;
            case Number(matchingBillAccount?.accountBalance):
              analyticsAmtText.push('Account Balance');
              break;
            case Number(matchingBillAccount?.fullPayBalance):
              analyticsAmtText.push('Full Pay');
              break;
            case Number(matchingBillAccount?.minimumAmountDueBreakdown?.previousAmountDue):
              analyticsAmtText.push('Past Due');
              break;
            case Number(matchingBillAccount?.minimumAmountDue) +
              Number(matchingBillAccount?.minimumAmountDueBreakdown?.currentAmountDue):
              analyticsAmtText.push('Past Due + Current');
              break;
            default:
              analyticsAmtText.push('Other Amount');
          }
        });
        this.sendAnalytics({
          pageAnalyticName: this.featureFlagService.isEnabled('simplified_payment_p2')
            ? 'Simplified:Complete'
            : 'Start',
          pageAnalyticSubCategory3: `${analyticsAmtText}`
        });
      });
  }

  getPaymentMethodById(id: string) {
    return this.store.select(PaymentMethodSelectors.selectPaymentMethod(id));
  }

  getPaymentMethodState() {
    return this.store.select(PaymentMethodSelectors.selectPaymentMethodState);
  }

  dispatchCorrelationId(correlationId: string) {
    this.store.dispatch(PaymentMethodActions.getPaymentMethods({ correlationId }));
  }

  selectIsPhoenixPolicy(): Observable<boolean> {
    const billingAccounts$ = this.store.select(OneTimePaySelectors.selectBillingAccounts);

    return billingAccounts$.pipe(
      mergeMap(billAccount =>
        this.store.select(OneTimePaySelectors.selectIsPolicyPhoenix(billAccount[0]))
      )
    );
  }

  getPaymentReviewInfo(): Observable<simplifiedPaymentReviewData> {
    return combineLatest([this.getBillAccountEntities(), this.getOTPEntities()]).pipe(
      map(([billAccounts, otpEntities]) => {
        const simplifiedBillAccounts: simplifiedPaymentReviewAccount[] = [];
        let sum = 0;
        for (const key in otpEntities) {
          if (otpEntities[key]) {
            const billAccount = billAccounts[otpEntities[key].billAccountNumber];
            sum += Math.round(otpEntities[key].selectedPaymentAmount * 100) / 100;
            simplifiedBillAccounts.push({
              nickName: billAccount.billingPreferences.accountNickName,
              risk: new ArrayToList().transform(
                _flatmap(billAccount.policyList, policy => policy.riskDescriptionList)
              ),
              amount: otpEntities[key].selectedPaymentAmount,
              billAccountNumber: billAccount.billAccountNumber
            });
          }
        }
        const number = Object.keys(otpEntities)[0];
        let heading: string;
        if (isSameDay(new Date(), otpEntities[number]?.selectedPaymentDate)) {
          heading = this.copyService.getCopy(
            'billing.simplifiedPayment.paymentConfirmationHeadingToday'
          );
        } else {
          heading = this.copyService.getCopy(
            `billing.simplifiedPayment.paymentConfirmationHeadingFuture`,
            {
              date: format(otpEntities[number]?.selectedPaymentDate, 'MM/DD/YYYY')
            }
          );
        }
        const obj: simplifiedPaymentReviewData = {
          selectedBillAccounts: simplifiedBillAccounts,
          paymentMethodNickName: otpEntities[number]?.selectedPaymentAccount,
          paymentDate: format(otpEntities[number]?.selectedPaymentDate, 'MM/DD/YYYY'),
          totalAmount: sum,
          confirmationHeading: heading
        };
        return obj;
      })
    );
  }

  goBackToAccountSelectionPage() {
    this.store.dispatch(fromRouterActions.Go({ path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT] }));
  }

  getPaymentTermsAndConditionsType(): PaymentTermsAndConditionsType {
    let paymentMethod: PaymentMethod;
    combineLatest([
      this.getPaymentReviewInfo(),
      this.store.select(PaymentMethodSelectors.selectPaymentMethods)
    ])
      .pipe(take(1))
      .subscribe(([data, paymentMethods]) => {
        paymentMethod = paymentMethods.find(
          payment => payment.nickName === data.paymentMethodNickName
        );
      });
    const paymentMethodType: PaymentMethodAccountType =
      this.paymentMethodUtil.getPaymentMethodAccountType(paymentMethod);
    return paymentMethodType === PaymentMethodAccountType.CREDIT_DEBIT
      ? PaymentTermsAndConditionsType.SCHEDULED_PAYMENT_CARD
      : PaymentTermsAndConditionsType.SCHEDULED_PAYMENT_BANK;
  }

  isPartner(): Observable<boolean> {
    return this.store.select(BrandSelectors.selectIsPartner);
  }

  getPaymentTermsAndConditionsPartnerContent() {
    return this.store.select(BrandSelectors.selectPaymentTermsAndConditionsPartnerContent);
  }

  leaveOTPFlow(billAccountNumber?: string, id?: string) {
    this.store.dispatch(OneTimePayActions.leaveOTPFlow({ billAccountNumber, id }));
  }

  preprocessPaymentPayload(): OTPReviewData[] {
    const pageData: OTPReviewData[] = [];
    this.getPaymentReviewInfo()
      .pipe(take(1))
      .subscribe(data => {
        for (const key in data.selectedBillAccounts) {
          if (data.selectedBillAccounts[key]) {
            pageData.push({
              billAccountNumber: data.selectedBillAccounts[key].billAccountNumber,
              selectedPaymentAccount: data.paymentMethodNickName,
              selectedPaymentDate: format(data.paymentDate, 'YYYY-MM-DD'),
              selectedPaymentAmount: data.selectedBillAccounts[key].amount
            });
          }
        }
      });
    return pageData;
  }

  getPaymentConfirmationDisclaimerContent(): Observable<string> {
    return combineLatest([
      this.isPartner(),
      this.getPaymentTermsAndConditionsPartnerContent()
    ]).pipe(
      map(([isPartner, partnerContent]) =>
        this.copyService.getCopy(`billing.simplifiedPayment.paymentConfirmationDisclaimer`, {
          companyName: isPartner
            ? partnerContent.companyName
            : this.copyService.getCopy('shared.amfamCompanyName'),
          phoneNumber: this.isPartner
            ? partnerContent.phoneNumber
            : this.copyService.getCopy('shared.amfamPhoneNumber'),
          landingPage: this.isPartner ? partnerContent.landingPage : env.links.myAccountURL,
          companyInfoPage: env.links.companyInfoPage
        })
      )
    );
  }

  goToAutoPaySetUp() {
    this.autoPayUtil.routeToMultipleAutoPaySelection('overview');
  }

  goToOverview() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/overview']
      })
    );
  }

  getBillAccountsEligibleForAutoPay(): Observable<BillAccount[]> {
    return this.store.select(AutoPayUtilSelectors.selectBillAccountsEligibleForAutoPay);
  }
}
